import { Grid } from '@mui/material';
import {
  ENTITY_TYPES,
  EntityGroupClass,
  Modal,
  createEntityGroupCallback,
  entityGroupState,
  entityLoaderState,
  getAllEntitiesGroupCallback,
  getEntityGroupByIdCallback,
  patchEntityGroupCallback,
  removeNullishOrEmptyFields,
} from '@laborability/commons';
import ModalForm from '../ModalForm';
import { LBTModal, LBTSelect, LBTTextField } from '@laborability/components';
import QueryBuilderForm from '../QueryBuilder';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { useEffect, useState } from 'react';
import { LABEL_ENTITY_TYPE } from './PageAction';

interface Props {
  id?: number;
}

export function EntitiesGroupForm({ id }: Props) {
  const postEntityGroup = useRecoilCallback(createEntityGroupCallback, []);
  const patchEntityGroup = useRecoilCallback(patchEntityGroupCallback, []);
  const getEntityGroup = useRecoilCallback(getEntityGroupByIdCallback, []);
  const getAllEntitiesGroups = useRecoilCallback(
    getAllEntitiesGroupCallback,
    [],
  );

  const [current, setCurrent] = useRecoilState(entityGroupState);
  const [modal, setModal] = useState<Modal | null>(null);

  const isLoading = useRecoilValue(entityLoaderState);

  const resetFormState = () => {
    setCurrent({
      ...new EntityGroupClass(),
      rules_schema: { combinator: 'and', not: false, rules: [] },
    });
  };

  useEffect(() => {
    if (id) getEntityGroup({ id });
    else resetFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <ModalForm
        defaultValues={{
          ...new EntityGroupClass(),
          rules_schema: { combinator: 'and', not: false, rules: [] },
        }}
        isLoading={isLoading}
        currentValues={current}
        validation={values => {
          return {
            ...(values.name === '' ? { name: 'Obbligatorio' } : {}),
            ...(!values.entity_type ? { entity_type: 'Obbligatorio' } : {}),
            ...(!values.rules_schema?.rules.length
              ? { rules_schema: 'Obbligatorio' }
              : {}),
          };
        }}
        handleSubmit={async values => {
          if (values.id) {
            await patchEntityGroup({
              id: values.id,
              ...removeNullishOrEmptyFields(values),
            });
            return;
          }
          await postEntityGroup({
            ...removeNullishOrEmptyFields(values),
            rules_name: values.name,
          });
          return;
        }}
        resetGridState={() => getAllEntitiesGroups()}
        resetFormState={resetFormState}
      >
        {({ values, errors, handleBlur, setFieldValue }) => (
          <>
            <Grid item mobile={12}>
              <LBTTextField
                label="Nome"
                type="text"
                name="name"
                onChange={value => setFieldValue('name', value)}
                onBlur={handleBlur}
                value={values.name}
                required
                error={!!errors?.name}
                helperText={errors?.name}
              />
            </Grid>

            <Grid item mobile={12}>
              <LBTSelect
                id="entity_type-select"
                name="entity_type"
                value={values.entity_type ?? undefined}
                label="Entità"
                handleChange={e => setFieldValue('entity_type', e)}
                onBlur={handleBlur}
                fullWidth
                required
                error={!!errors?.entity_type}
                helperText={errors?.entity_type}
                items={ENTITY_TYPES.map(item => ({
                  id: item,
                  name: LABEL_ENTITY_TYPE[item],
                }))}
              />
            </Grid>

            {!!values.entity_type && (
              <Grid item mobile={12}>
                <QueryBuilderForm
                  query={values.rules_schema as any}
                  errors={errors}
                  entityType={values.entity_type}
                  onQueryChange={value =>
                    setFieldValue('rules_schema', value, false)
                  }
                />
              </Grid>
            )}
          </>
        )}
      </ModalForm>
      {modal && (
        <LBTModal
          open={!!modal}
          onXClose={() => setModal(null)}
          {...modal}
          modalStyle={{
            width: 'fit-content',
            maxWidth: '1000px',
            minWidth: '600px',
          }}
        />
      )}
    </>
  );
}
